import React, { useRef } from 'react';
import { accessibleOnClick } from '../../utils/accessible-on-click';
import { ModifySlot } from './types';
import ScheduleTimeField from './schedule-time-field';
import { CircleCrossIcon } from '../../svg/circle-cross-icon';

export type ScheduleTimeSlotPillProps = {
  day: number;
  slotIndex: number;
  slot: ModifySlot;
  daySlots: ModifySlot[];
  canEdit: boolean;
  onSlotChange: (day: number, slotIndex: number, slot: ModifySlot) => void;
  onSlotBlur: () => void;
  onFieldFocus: (day: number) => void;
  onRemove: (day: number, slotIndex: number) => void;
  autoFocusStart?: boolean;
  onAutoFocusHandled?: () => void;
};

const ScheduleTimeSlotPill: React.FC<ScheduleTimeSlotPillProps> = ({
  day,
  slotIndex,
  slot,
  daySlots,
  canEdit,
  onSlotChange,
  onSlotBlur,
  onFieldFocus,
  onRemove,
  autoFocusStart = false,
  onAutoFocusHandled,
}) => {
  const endFieldInputRef = useRef<HTMLInputElement>(null);
  const hasError = slot.invalidDataError || slot.overlapError;

  const updateSlot = (partial: Partial<ModifySlot>) => {
    onSlotChange(day, slotIndex, { ...slot, ...partial });
  };

  const clearSlotErrors = () => {
    if (slot.invalidDataError || slot.overlapError) {
      onSlotChange(day, slotIndex, {
        ...slot,
        invalidDataError: false,
        overlapError: false,
      });
    }
  };

  const handleFocusLeave = (e: React.FocusEvent<HTMLElement>) => {
    const pill = e.currentTarget.closest('.bs-schedule-form__slot-pill');
    const nextTarget = e.relatedTarget;

    if (pill && nextTarget instanceof Node && pill.contains(nextTarget)) {
      return;
    }

    onSlotBlur();
  };

  const handleFieldFocus = () => {
    onFieldFocus(day);
    clearSlotErrors();
  };

  const handleFieldBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    handleFocusLeave(e);
  };

  return (
    <div
      className={`bs-schedule-form__slot-pill${
        hasError ? ' bs-schedule-form__slot-pill--error' : ''
      }`}
    >
      <span className="bs-schedule-form__slot-label regular-2 gray-txt-11">
        From
      </span>
      <ScheduleTimeField
        fieldRole="start"
        slotIndex={slotIndex}
        daySlots={daySlots}
        value={slot.start}
        onChange={(hour, minute) => updateSlot({ start: { hour, minute } })}
        onBlur={handleFieldBlur}
        onFocus={handleFieldFocus}
        onComplete={() => {
          requestAnimationFrame(() => {
            endFieldInputRef.current?.focus();
          });
        }}
        disabled={!canEdit}
        hasError={hasError}
        autoFocusOnMount={autoFocusStart}
        onAutoFocusHandled={onAutoFocusHandled}
      />
      <span className="bs-schedule-form__slot-dash">-</span>
      <span className="bs-schedule-form__slot-label regular-2 gray-txt-11">
        To
      </span>
      <ScheduleTimeField
        fieldRole="end"
        slotIndex={slotIndex}
        daySlots={daySlots}
        value={slot.end}
        onChange={(hour, minute) => updateSlot({ end: { hour, minute } })}
        onBlur={handleFieldBlur}
        onFocus={handleFieldFocus}
        inputRef={endFieldInputRef}
        disabled={!canEdit}
        hasError={hasError}
      />
      {canEdit && (
        <button
          type="button"
          className="bs-schedule-form__slot-remove"
          aria-label="Remove time slot"
          onBlur={handleFocusLeave}
          {...accessibleOnClick(() => onRemove(day, slotIndex))}
        >
          {/* <Icon identifier="close-o" /> */}
          <CircleCrossIcon
            width={14}
            height={14}
            className="bs-schedule-form__slot-remove-icon"
          />
        </button>
      )}
    </div>
  );
};

export default ScheduleTimeSlotPill;
